import api from "@/lib/axiosConfig.ts";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const CONV_URL = import.meta.env.VITE_CONVERTISSEUR_URL;

/**
 * Upload a cover page image to S3 via the convertisseur.
 * Both sizes (large + moyen) are always generated so user can change imageSize later.
 *
 * @param file - The image file to upload
 * @param s3Bucket - S3 bucket name
 * @param instanceId - Instance ID
 * @param oldLink - If provided, replaces the existing image (same link, new files)
 * @returns The imageLink to store in CoverPage
 */
export async function uploadCoverPageImage(
  file: File,
  s3Bucket: string,
  instanceId: string,
  oldLink?: string,
): Promise<{ link: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("S3_BUCKET", s3Bucket);
  formData.append("instanceId", instanceId);
  if (oldLink) formData.append("oldLink", oldLink);
  const { data } = await axios.post(CONV_URL + "uploadV3/cover-page", formData);
  return { link: data.link };
}

export type CoverPageImageSize = "large" | "small";
export type CoverPageImagePosition = "header" | "after_title" | "after_para1" | "footer";

export interface CoverPage {
  id: string;
  name: string;
  printType?: string | null;
  title: string;
  paragraph1?: string | null;
  paragraph2?: string | null;
  imageLink?: string | null;
  imageUrl?: string | null;
  imageSize?: CoverPageImageSize | null;
  imagePosition?: CoverPageImagePosition | null;
  instanceId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCoverPageDto {
  name: string;
  printType?: string;
  title: string;
  paragraph1?: string;
  paragraph2?: string;
  imageLink?: string;
  imageSize?: CoverPageImageSize;
  imagePosition?: CoverPageImagePosition;
}

export interface UpdateCoverPageDto {
  name?: string;
  printType?: string;
  title?: string;
  paragraph1?: string;
  paragraph2?: string;
  /** Set to null to remove the image, undefined to keep existing */
  imageLink?: string | null;
  imageSize?: CoverPageImageSize;
  imagePosition?: CoverPageImagePosition;
}

const COVER_PAGES_KEY = "cover-pages";

export const useGetCoverPages = (printType?: string) =>
  useQuery({
    queryKey: [COVER_PAGES_KEY, printType],
    queryFn: async () => {
      const params = printType ? { printType } : {};
      const { data } = await api.get<CoverPage[]>("/back/api/cover-pages", { params });
      return data;
    },
  });

export const useCreateCoverPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCoverPageDto) => {
      const { data } = await api.post<CoverPage>("/back/api/cover-pages", dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COVER_PAGES_KEY] });
    },
  });
};

export const useUpdateCoverPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateCoverPageDto }) => {
      const { data } = await api.patch<CoverPage>(`/back/api/cover-pages/${id}`, dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COVER_PAGES_KEY] });
    },
  });
};

export const useDeleteCoverPage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/back/api/cover-pages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COVER_PAGES_KEY] });
    },
  });
};
